import { checkUserExist, login, sendOTP, verifyOTP } from "@/api";
import { useCountDown } from "@/hooks/useCountdown";
import { IMessage } from "@/lib/interfaces/IMessage";
import { IProfile } from "@/lib/interfaces/IProfile";
import { IVerify } from "@/lib/interfaces/IVerify";
import { inter, manrope, poppins } from "@/styles/font";
import {
  Button,
  Center,
  Flex,
  HStack,
  PinInput,
  PinInputField,
  Text,
  useToast,
} from "@chakra-ui/react";
import { AxiosError } from "axios";
import Image from "next/image";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";

import logoBag from "../../public/logo-bag.png";
import { SignUpParams } from "./signin";
import { IUser } from "@/lib/interfaces/IUser";
import { getCookie, setCookie } from "cookies-next";
import { UserContext } from "@/userContext";
import { NextSeo } from "next-seo";

interface VerifyParams {
  serviceID: string;
  otp: string;
}

const Verify = () => {
  const { countDown, isComplete, setIsComplete, setCountDown } =
    useCountDown(59);
  const toast = useToast();
  const router = useRouter();
  const profile = useContext(UserContext);
  const [pinValue, setPinValue] = useState<string>("");
  const [pinError, setPinError] = useState<string | null>(null);
  const { serviceid: serviceID, phone } = router.query;

  useEffect(() => { }, [router.query.slug])

  // VERIFY MUTATION
  const { mutate, isLoading } = useMutation<IVerify, AxiosError, VerifyParams>(
    async ({ serviceID, otp }) => verifyOTP(serviceID, otp),
    {
      onSuccess(data, variables, context) {
        if (data.status) {
          // VERIFICATION SUCCESSFUL
          const modPhone = phone as string;
          checkUserMutate({ phone: modPhone });
        } else {
          setPinError(data.message);
        }
      },
      onError(error, variables, context) {
        const errData = error.response?.data as IVerify;
        setPinError(errData.message);
      },
    }
  );

  // RESEND CODE MUTATION
  const { mutate: resendMutate } = useMutation<
    IMessage,
    AxiosError,
    SignUpParams
  >(async ({ phone }) => sendOTP(phone), {
    onSuccess(data, variables, context) {
      router.replace(
        {
          pathname: "/verify",
          query: { serviceid: data.serviceID, phone },
        },
        `/verify?serviceID=${data.serviceID}`
      );
    },
  });

  // USER EXISTS MUTATION
  const { mutate: checkUserMutate } = useMutation<
    IUser,
    AxiosError,
    SignUpParams
  >(async ({ phone }) => checkUserExist(phone), {
    onSuccess(data, variables, context) {
      if (data.message) {
        // USER EXISTS
        toast({
          title: 'Verification successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right'
        })
        const modPhone = phone as string;
        loginMutate({ phone: modPhone });
      }
    },
    onError(error, variables, context) {
      setPinError("This account does not exist!")
      router.replace("/register");
    },
  });

  // LOGIN MUTATION
  const { mutate: loginMutate } = useMutation<
    IProfile,
    AxiosError,
    SignUpParams
  >(async ({ phone }) => login(phone), {
    onSuccess(data, variables, context) {
      const MAX_AGE = 32000000; // SLIGHTLY ABOVE A YEAR :(
      // const isSecure = process.env.NODE_ENV !== "production";

      // document.cookie = serialize("martline-token", data.token, {
      //   sameSite: "strict",
      //   maxAge: MAX_AGE,
      //   httpOnly: true,
      //   secure: isSecure,
      // });
      setCookie("martline-user", data, {
        path: "/",
        maxAge: MAX_AGE,
        sameSite: "strict"
      })
      const cookie = getCookie("martline-user", {
        path: "/",
        sameSite: "strict"
      });

      const json = JSON.parse(cookie!!.toString());
      profile.setUser(json);
      router.replace("/");
    },
    onError(error, variables, context) {
      const errData = error.response?.data as { message: string };
      setPinError(errData.message);
    },
  });

  return (
    <Center h={"full"} px={8} py={24}>
      <NextSeo
        title={'Verify | Martlines'}
        description={''} />
      <Flex flexDirection={"column"}>
        <Flex flexDirection={"column"} alignItems={"center"}>
          <Image src={logoBag} alt={"Martlines logo"} width={50} />
          <Text
            className={poppins.className}
            fontSize={"24px"}
            fontWeight={"semibold"}
            color={"martlinegray.900"}
            mt={10}
          >
            Verification
          </Text>

          <Text
            color={"martlinegray.600"}
            fontSize={"md"}
            fontWeight={"medium"}
            className={inter.className}
            textAlign={"center"}
            mt={3}
            px={8}
          >
            Enter 6-digit code sent to you on
          </Text>

          <Text
            className={inter.className}
            fontWeight={"semibold"}
            color={"martlinegray.600"}
          >
            {phone}
          </Text>

          <HStack
            spacing={{ base: 5, lg: 10 }}
            mt={10}
            className={poppins.className}
            fontWeight={"light"}
          >
            <PinInput
              placeholder={"0"}
              variant={"flushed"}
              type={"number"}
              focusBorderColor={"martlinegray.500"}
              onComplete={(value) => setPinValue(value)}
              onChange={() => setPinError(null)}
            >
              <PinInputField
                borderColor={"martlinegray.400"}
                fontSize={{ base: "32px", lg: "40px" }}
                color={"martlinegray.500"}
              />
              <PinInputField
                borderColor={"martlinegray.400"}
                fontSize={{ base: "32px", lg: "40px" }}
                color={"martlinegray.500"}
              />
              <PinInputField
                borderColor={"martlinegray.400"}
                fontSize={{ base: "32px", lg: "40px" }}
                color={"martlinegray.500"}
              />
              <PinInputField
                borderColor={"martlinegray.400"}
                fontSize={{ base: "32px", lg: "40px" }}
                color={"martlinegray.500"}
              />
              <PinInputField
                borderColor={"martlinegray.400"}
                fontSize={{ base: "32px", lg: "40px" }}
                color={"martlinegray.500"}
              />
              <PinInputField
                borderColor={"martlinegray.400"}
                fontSize={{ base: "32px", lg: "40px" }}
                color={"martlinegray.500"}
              />
            </PinInput>
          </HStack>
          {pinError && (
            <Text mt={1} className={poppins.className} color={"red.400"}>
              {pinError}
            </Text>
          )}
        </Flex>

        <Flex alignSelf={"end"}>
          {isComplete ? (
            <Button
              mt={2}
              variant={"ghost"}
              className={manrope.className}
              onClick={() => {
                setIsComplete(false);
                setCountDown(59);
                const modPhone = phone as string;
                resendMutate({ phone: modPhone });
                // if (modPhone?.startsWith("+234"))
                // else resendMutate({ phone: `+234${phone}` });
              }}
            >
              Resend code
            </Button>
          ) : (
            <Text
              fontWeight={"normal"}
              mt={3}
              className={manrope.className}
              color={"martlinegray.600"}
            >
              Re-send code in{" "}
              <Text fontWeight={"semibold"} color={"success.600"} as={"span"}>
                {"00"}:
                {countDown.toLocaleString("en-US", {
                  minimumIntegerDigits: 2,
                })}
              </Text>
            </Text>
          )}
        </Flex>

        <Button
          isLoading={isLoading}
          loadingText={"Please wait..."}
          variant={"solid"}
          py={"22px"}
          px={"56px"}
          // transform={"translate(1%)"}
          className={poppins.className}
          mt={10}
          onClick={() => {
            if (pinValue.length < 6) {
              setPinError("Invalid verification code!");
              return;
            }
            mutate({ serviceID: serviceID as string, otp: pinValue });
          }}
        >
          Verify Number
        </Button>
      </Flex>
    </Center>
  );
};

export default Verify;
